import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Flame, Play, Users, Eye, Heart, Music, Check } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { usePT } from '../utils/promoteTranslations';

const PromoteProfileModal = ({ onClose }) => {
  const t = usePT();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/promote-profile/status');
      setStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Handle Stripe redirect return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (params.get('promo') === 'success' && sid) {
      (async () => {
        try {
          const res = await axios.post('/api/promote-profile/confirm', { session_id: sid });
          if (res.data.paid || res.data.already_active) {
            toast.success(t('promote.successToast', { date: (res.data.ends_at || '').split('T')[0] || '' }));
            window.history.replaceState({}, '', window.location.pathname);
            load();
          }
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, []);

  const startCheckout = async () => {
    if (!window.confirm(t('promote.confirmPay'))) return;
    setStarting(true);
    try {
      const res = await axios.post('/api/promote-profile/checkout', {});
      const url = res.data.checkout_url;
      const sid = res.data.session_id;
      if (!url) throw new Error('no url');

      if (Capacitor.isNativePlatform()) {
        // On mobile: open Stripe in in-app browser; poll for payment_status
        // then auto-close the browser and confirm the promotion.
        await Browser.open({ url, presentationStyle: 'popover' });

        // Poll every 3s for up to ~5 minutes to detect payment completion
        let attempts = 0;
        const iv = setInterval(async () => {
          attempts += 1;
          try {
            const r = await axios.post('/api/promote-profile/confirm', { session_id: sid });
            if (r.data.paid || r.data.already_active) {
              clearInterval(iv);
              try { await Browser.close(); } catch (_) {}
              toast.success(t('promote.successToast', { date: (r.data.ends_at || '').split('T')[0] || '' }));
              load();
              setStarting(false);
            }
          } catch (_) { /* keep polling */ }
          if (attempts > 100) {
            clearInterval(iv);
            setStarting(false);
          }
        }, 3000);
      } else {
        // Web: full redirect to Stripe
        window.location.href = url;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || t('promote.alreadyActive');
      toast.error(msg);
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6" data-testid="promote-profile-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-magenta-600 rounded-full">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold">{t('promote.modalTitle')}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="promote-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-6">{t('promote.modalSubtitle')}</p>

        {loading ? (
          <div className="text-center py-8 text-gray-400">…</div>
        ) : status?.active ? (
          <div>
            <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-500/15 to-magenta-500/15 border border-orange-500/30 mb-4">
              <p className="text-orange-300 font-semibold mb-1 flex items-center gap-2">
                <Check className="w-4 h-4" /> {t('promote.activePromoLabel')}
              </p>
              <p className="text-sm text-gray-300">
                {t('promote.daysLeft', { n: status.days_left ?? '-' })}
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {t('promote.followersGained', { n: status.followers_gained ?? 0 })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 rounded-full font-semibold"
            >
              {t('promote.close')}
            </button>
          </div>
        ) : (
          <div>
            <div className="mb-4 p-4 rounded-2xl bg-white/5 border border-white/10">
              <p className="font-semibold mb-3">{t('promote.howItWorksTitle')}</p>
              <ul className="text-sm text-gray-300 space-y-2">
                <li className="flex gap-2"><span className="text-orange-400">1.</span> <span>{t('promote.howItWorks1')}</span></li>
                <li className="flex gap-2"><span className="text-orange-400">2.</span> <span>{t('promote.howItWorks2')}</span></li>
                <li className="flex gap-2"><span className="text-orange-400">3.</span> <span>{t('promote.howItWorks3')}</span></li>
              </ul>
            </div>
            <div className="flex items-baseline justify-center gap-3 mb-6">
              <span className="text-4xl font-black bg-gradient-to-r from-orange-400 to-magenta-500 bg-clip-text text-transparent">{t('promote.price')}</span>
              <span className="text-gray-400">· {t('promote.duration')}</span>
            </div>
            <button
              onClick={startCheckout}
              disabled={starting}
              className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-magenta-600 hover:from-orange-600 hover:to-magenta-700 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="promote-pay-btn"
            >
              <Flame className="w-4 h-4" />
              {starting ? t('promote.paying') : t('promote.payBtn')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoteProfileModal;
